// ===========================================
// Prompt Revision Modal - Quick Iteration Flow
// ===========================================
// A focused, 2-step flow for quickly revising existing prompts:
// 1. Capture intent - What would you like to change?
// 2. Review & Refine - Diff view, inline edit, or AI refinement
// Then opens PromptModal to save as new version

import React, { useState, useRef, useEffect } from 'react';
import * as Diff from 'diff';
import {
  X,
  ChevronDown,
  ChevronUp,
  Wand2,
  Loader,
  ArrowLeft,
  RefreshCw,
  Check,
  Minimize2,
  Maximize2,
  ListPlus,
  FileText,
  Shield,
  Sparkles,
  MessageSquare,
  Zap,
  Eye,
  Edit3
} from 'lucide-react';
import { promptExpertAPI } from '../services/api';
import './PromptRevisionModal.scss';

// ===========================================
// Quick Action Chips - Common revision intents
// ===========================================
const QUICK_ACTIONS = [
  { id: 'simplify', label: 'Simplify', icon: Minimize2, prompt: 'Make this prompt more concise and easier to understand. Remove redundancy while keeping core requirements.' },
  { id: 'expand', label: 'Expand', icon: Maximize2, prompt: 'Expand this prompt with more detail, context, and specificity. Add examples where helpful.' },
  { id: 'examples', label: 'Add Examples', icon: ListPlus, prompt: 'Add concrete examples to illustrate the expected input/output or behavior.' },
  { id: 'constraints', label: 'Add Constraints', icon: Shield, prompt: 'Add clear constraints and boundaries. Specify what should NOT be done and edge cases to handle.' },
  { id: 'structure', label: 'Better Structure', icon: FileText, prompt: 'Improve the structure and formatting. Use clear sections, headers, and bullet points.' },
  { id: 'tone', label: 'Professional Tone', icon: MessageSquare, prompt: 'Adjust the tone to be more professional and precise. Use clear, direct language.' },
  { id: 'creative', label: 'More Creative', icon: Sparkles, prompt: 'Make this prompt encourage more creative, innovative, or out-of-the-box solutions.' },
  { id: 'strict', label: 'Stricter Output', icon: Zap, prompt: 'Add stricter output format requirements. Be very specific about the expected response structure.' }
];

// ===========================================
// Refinement Chips - Subset for quick refinements
// ===========================================
const REFINEMENT_CHIPS = [
  { id: 'clarify', label: 'Clarify', prompt: 'Make the wording clearer and less ambiguous.' },
  { id: 'shorter', label: 'Shorter', prompt: 'Make it more concise without losing meaning.' },
  { id: 'detailed', label: 'More Detail', prompt: 'Add more specific details and context.' },
  { id: 'fix-tone', label: 'Fix Tone', prompt: 'Adjust the tone to be more appropriate and consistent.' }
];

// ===========================================
// Main Component
// ===========================================
const PromptRevisionModal = ({ prompt, onSave, onClose }) => {
  // ===========================================
  // State
  // ===========================================
  const [step, setStep] = useState(1); // 1 = intent, 2 = review & refine
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [revisionIntent, setRevisionIntent] = useState('');
  const [selectedChips, setSelectedChips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revisedContent, setRevisedContent] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  
  // Step 2: Review & Refine state
  const [isEditMode, setIsEditMode] = useState(false);          // Toggle between diff view and edit mode
  const [editableContent, setEditableContent] = useState('');   // Content in edit mode
  const [showRefinement, setShowRefinement] = useState(false);  // Toggle refinement panel
  const [refinementIntent, setRefinementIntent] = useState(''); // Refinement text input
  const [refinementChips, setRefinementChips] = useState([]);   // Selected refinement chips
  const [refining, setRefining] = useState(false);              // Refinement loading state
  
  // Refs
  const intentInputRef = useRef(null);
  const editTextareaRef = useRef(null);
  
  // Focus input on mount
  useEffect(() => {
    if (step === 1 && intentInputRef.current) {
      intentInputRef.current.focus();
    }
  }, [step]);

  // Sync editableContent when switching to edit mode
  useEffect(() => {
    if (isEditMode) {
      setEditableContent(revisedContent);
      // Focus the textarea after a brief delay
      setTimeout(() => editTextareaRef.current?.focus(), 100);
    }
  }, [isEditMode, revisedContent]);

  // ===========================================
  // Toggle Quick Action Chip
  // ===========================================
  const toggleChip = (chipId) => {
    setSelectedChips(prev => 
      prev.includes(chipId) 
        ? prev.filter(id => id !== chipId)
        : [...prev, chipId]
    );
  };

  // Toggle Refinement Chip
  const toggleRefinementChip = (chipId) => {
    setRefinementChips(prev => 
      prev.includes(chipId) 
        ? prev.filter(id => id !== chipId)
        : [...prev, chipId]
    );
  };

  // ===========================================
  // Build Revision Prompt for AI
  // ===========================================
  const buildRevisionPrompt = () => {
    let instructions = [];
    
    // Add selected chip instructions
    selectedChips.forEach(chipId => {
      const chip = QUICK_ACTIONS.find(c => c.id === chipId);
      if (chip) instructions.push(chip.prompt);
    });
    
    // Add custom instruction
    if (revisionIntent.trim()) {
      instructions.push(revisionIntent.trim());
    }
    
    return instructions.join('\n\n');
  };

  // Build Refinement Prompt for AI
  const buildRefinementPrompt = () => {
    let instructions = [];
    
    // Add selected refinement chip instructions
    refinementChips.forEach(chipId => {
      const chip = REFINEMENT_CHIPS.find(c => c.id === chipId);
      if (chip) instructions.push(chip.prompt);
    });
    
    // Add custom refinement instruction
    if (refinementIntent.trim()) {
      instructions.push(refinementIntent.trim());
    }
    
    return instructions.join('\n\n');
  };

  // ===========================================
  // Generate Revised Prompt (Initial)
  // ===========================================
  const handleGenerate = async () => {
    const instructions = buildRevisionPrompt();
    
    if (!instructions) {
      setError('Please select an action or describe what you want to change.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const revisionMessage = `I have an existing prompt that I want to revise. Please apply the following changes and return ONLY the revised prompt (no explanations, no markdown code blocks, just the raw revised prompt text).

## CURRENT PROMPT:
${prompt.content}

## CHANGES TO MAKE:
${instructions}

## INSTRUCTIONS:
1. Apply all the requested changes to the prompt above
2. Maintain the overall structure and intent of the original
3. Return ONLY the revised prompt text - no explanations, no code blocks, no headers
4. Preserve any specific technical details or constraints from the original unless asked to change them`;

      const response = await promptExpertAPI.chat({
        message: revisionMessage,
        conversationHistory: [],
        context: {
          targetRole: prompt.role,
          targetAction: prompt.action
        }
      });
      
      if (response?.data?.success && response?.data?.data?.message) {
        let revised = response.data.data.message;
        revised = revised.replace(/^```(?:markdown)?\n?/i, '').replace(/\n?```$/i, '');
        revised = revised.trim();
        
        setRevisedContent(revised);
        setEditableContent(revised);
        setChangeSummary(`Applied: ${selectedChips.map(id => QUICK_ACTIONS.find(c => c.id === id)?.label).filter(Boolean).join(', ')}${revisionIntent.trim() ? (selectedChips.length ? ' + ' : '') + 'custom changes' : ''}`);
        setStep(2);
      } else {
        throw new Error(response?.data?.error || 'Failed to generate revision');
      }
    } catch (err) {
      console.error('Revision error:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to generate revision. Please try again.');
    }
    
    setLoading(false);
  };

  // ===========================================
  // Apply Refinement (AI-assisted)
  // ===========================================
  const handleRefinement = async () => {
    const instructions = buildRefinementPrompt();
    
    if (!instructions) {
      setError('Please select a refinement option or describe what to refine.');
      return;
    }
    
    setRefining(true);
    setError(null);
    
    // Use current content (either from edit mode or revisedContent)
    const currentContent = isEditMode ? editableContent : revisedContent;
    
    try {
      const refinementMessage = `I have a prompt that I want to refine further. Please apply the following refinements and return ONLY the refined prompt (no explanations, no markdown code blocks, just the raw refined prompt text).

## CURRENT PROMPT:
${currentContent}

## REFINEMENTS TO APPLY:
${instructions}

## INSTRUCTIONS:
1. Apply the refinements to improve the prompt
2. Keep the core intent and structure intact
3. Return ONLY the refined prompt text - no explanations, no code blocks
4. Make minimal changes - only what's needed for the refinement`;

      const response = await promptExpertAPI.chat({
        message: refinementMessage,
        conversationHistory: [],
        context: {
          targetRole: prompt.role,
          targetAction: prompt.action
        }
      });
      
      if (response?.data?.success && response?.data?.data?.message) {
        let refined = response.data.data.message;
        refined = refined.replace(/^```(?:markdown)?\n?/i, '').replace(/\n?```$/i, '');
        refined = refined.trim();
        
        setRevisedContent(refined);
        setEditableContent(refined);
        
        // Update summary
        const refinementLabels = refinementChips.map(id => REFINEMENT_CHIPS.find(c => c.id === id)?.label).filter(Boolean);
        setChangeSummary(prev => `${prev} → Refined: ${refinementLabels.join(', ')}${refinementIntent.trim() ? (refinementLabels.length ? ' + ' : '') + 'custom refinement' : ''}`);
        
        // Reset refinement inputs
        setRefinementIntent('');
        setRefinementChips([]);
        setShowRefinement(false);
        setIsEditMode(false); // Switch back to diff view to see changes
      } else {
        throw new Error(response?.data?.error || 'Failed to apply refinement');
      }
    } catch (err) {
      console.error('Refinement error:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to apply refinement. Please try again.');
    }
    
    setRefining(false);
  };

  // ===========================================
  // Apply Manual Edits
  // ===========================================
  const handleApplyEdits = () => {
    setRevisedContent(editableContent);
    setChangeSummary(prev => `${prev} → Manual edits applied`);
    setIsEditMode(false);
  };

  // ===========================================
  // Handle Save - Pass to parent with new content
  // ===========================================
  const handleSave = () => {
    // Use editableContent if in edit mode, otherwise revisedContent
    const finalContent = isEditMode ? editableContent : revisedContent;
    onSave({
      ...prompt,
      content: finalContent,
      title: prompt.title ? `${prompt.title} (v2)` : 'Revised Prompt',
      createdByAI: true
    });
  };

  // ===========================================
  // Regenerate with same settings
  // ===========================================
  const handleRegenerate = () => {
    handleGenerate();
  };

  // ===========================================
  // Go back to intent step
  // ===========================================
  const handleBack = () => {
    setStep(1);
    setError(null);
    setIsEditMode(false);
    setShowRefinement(false);
  };

  // ===========================================
  // Truncate text for preview
  // ===========================================
  const truncateText = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // ===========================================
  // Render Step 1: Capture Intent
  // ===========================================
  const renderIntentStep = () => (
    <div className="prompt-revision__content">
      {/* Prompt Info - Title & Description */}
      <div className="prompt-revision__info">
        <div className="prompt-revision__info-title">
          {prompt.title}
        </div>
        {prompt.description && (
          <div className="prompt-revision__info-description">
            {prompt.description}
          </div>
        )}
      </div>

      {/* Current Prompt Content Preview */}
      <div className="prompt-revision__preview">
        <button 
          className="prompt-revision__preview-toggle"
          onClick={() => setShowFullPrompt(!showFullPrompt)}
        >
          <span>Current Prompt Content</span>
          {showFullPrompt ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        <div className={`prompt-revision__preview-content ${showFullPrompt ? 'expanded' : ''}`}>
          <code>{showFullPrompt ? prompt.content : truncateText(prompt.content, 300)}</code>
        </div>
      </div>

      {/* Revision Intent Input */}
      <div className="prompt-revision__intent">
        <label>What would you like to change?</label>
        <textarea
          ref={intentInputRef}
          value={revisionIntent}
          onChange={(e) => setRevisionIntent(e.target.value)}
          placeholder="e.g., 'Make it more concise' or 'Add error handling requirements' or 'Focus more on performance optimization'"
          rows={3}
          maxLength={500}
        />
        <div className="prompt-revision__char-count">
          {revisionIntent.length} / 500
        </div>
      </div>

      {/* Quick Action Chips */}
      <div className="prompt-revision__chips">
        <label>Quick Actions</label>
        <div className="prompt-revision__chips-grid">
          {QUICK_ACTIONS.map(chip => {
            const Icon = chip.icon;
            const isSelected = selectedChips.includes(chip.id);
            return (
              <button
                key={chip.id}
                className={`prompt-revision__chip ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleChip(chip.id)}
                title={chip.prompt}
              >
                <Icon size={14} />
                {chip.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="prompt-revision__error">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="prompt-revision__actions">
        <button 
          className="prompt-revision__btn prompt-revision__btn--cancel"
          onClick={onClose}
        >
          Cancel
        </button>
        <button 
          className="prompt-revision__btn prompt-revision__btn--generate"
          onClick={handleGenerate}
          disabled={loading || (!revisionIntent.trim() && selectedChips.length === 0)}
        >
          {loading ? (
            <>
              <Loader size={16} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Wand2 size={16} />
              Generate Revision
            </>
          )}
        </button>
      </div>
    </div>
  );

  // ===========================================
  // Compute Diff between original and revised
  // ===========================================
  const computeDiff = () => {
    return Diff.diffWords(prompt.content, revisedContent);
  };

  // ===========================================
  // Render Step 2: Review & Refine
  // ===========================================
  const renderReviewStep = () => {
    const diffParts = computeDiff();
    
    return (
      <div className="prompt-revision__content">
        {/* Mode Toggle: Diff View / Edit Mode */}
        <div className="prompt-revision__mode-toggle">
          <button 
            className={`prompt-revision__mode-btn ${!isEditMode ? 'active' : ''}`}
            onClick={() => setIsEditMode(false)}
          >
            <Eye size={14} />
            Diff View
          </button>
          <button 
            className={`prompt-revision__mode-btn ${isEditMode ? 'active' : ''}`}
            onClick={() => setIsEditMode(true)}
          >
            <Edit3 size={14} />
            Edit Mode
          </button>
        </div>

        {/* Content Area - Diff or Edit */}
        {isEditMode ? (
          // Edit Mode - Editable textarea
          <div className="prompt-revision__edit">
            <textarea
              ref={editTextareaRef}
              value={editableContent}
              onChange={(e) => setEditableContent(e.target.value)}
              className="prompt-revision__edit-textarea"
              placeholder="Edit the revised prompt..."
            />
            <div className="prompt-revision__edit-actions">
              <button 
                className="prompt-revision__btn prompt-revision__btn--apply-edits"
                onClick={handleApplyEdits}
                disabled={editableContent === revisedContent}
              >
                <Check size={14} />
                Apply Edits
              </button>
            </div>
          </div>
        ) : (
          // Diff View - IDE style
          <div className="prompt-revision__diff">
            <div className="prompt-revision__diff-header">
              <span className="prompt-revision__diff-legend">
                <span className="prompt-revision__diff-legend-item prompt-revision__diff-legend-item--added">Added</span>
                <span className="prompt-revision__diff-legend-item prompt-revision__diff-legend-item--removed">Removed</span>
                <span className="prompt-revision__diff-legend-item">Unchanged</span>
              </span>
            </div>
            <div className="prompt-revision__diff-content">
              <code>
                {diffParts.map((part, index) => {
                  const className = part.added 
                    ? 'prompt-revision__diff-added' 
                    : part.removed 
                      ? 'prompt-revision__diff-removed' 
                      : '';
                  return (
                    <span key={index} className={className}>
                      {part.value}
                    </span>
                  );
                })}
              </code>
            </div>
          </div>
        )}

        {/* Refinement Panel - Collapsible */}
        <div className={`prompt-revision__refinement ${showRefinement ? 'open' : ''}`}>
          <button 
            className="prompt-revision__refinement-toggle"
            onClick={() => setShowRefinement(!showRefinement)}
          >
            <Wand2 size={14} />
            <span>Refine with AI</span>
            {showRefinement ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          
          {showRefinement && (
            <div className="prompt-revision__refinement-content">
              <textarea
                value={refinementIntent}
                onChange={(e) => setRefinementIntent(e.target.value)}
                placeholder="Describe additional changes, e.g., 'Make the introduction shorter' or 'Add more specific examples'"
                rows={2}
                maxLength={300}
              />
              
              <div className="prompt-revision__refinement-chips">
                {REFINEMENT_CHIPS.map(chip => {
                  const isSelected = refinementChips.includes(chip.id);
                  return (
                    <button
                      key={chip.id}
                      className={`prompt-revision__refinement-chip ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleRefinementChip(chip.id)}
                      title={chip.prompt}
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>
              
              <button 
                className="prompt-revision__btn prompt-revision__btn--refine"
                onClick={handleRefinement}
                disabled={refining || (!refinementIntent.trim() && refinementChips.length === 0)}
              >
                {refining ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Refining...
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    Apply Refinement
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Change Summary */}
        <div className="prompt-revision__summary">
          <Sparkles size={14} />
          <span>{changeSummary}</span>
        </div>

        {/* Error Message */}
        {error && (
          <div className="prompt-revision__error">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="prompt-revision__actions">
          <button 
            className="prompt-revision__btn prompt-revision__btn--back"
            onClick={handleBack}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <div className="prompt-revision__actions-right">
            <button 
              className="prompt-revision__btn prompt-revision__btn--regenerate"
              onClick={handleRegenerate}
              disabled={loading || refining}
            >
              {loading ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              Regenerate
            </button>
            <button 
              className="prompt-revision__btn prompt-revision__btn--save"
              onClick={handleSave}
              disabled={loading || refining}
            >
              <Check size={16} />
              Save as New Version
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===========================================
  // Render
  // ===========================================
  return (
    <div className="prompt-revision__overlay" onClick={onClose}>
      <div className="prompt-revision__modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="prompt-revision__header">
          <div className="prompt-revision__header-content">
            <Wand2 size={18} />
            <div>
              <h3>Revise Prompt</h3>
              <span className="prompt-revision__title">{prompt.title}</span>
            </div>
          </div>
          <button className="prompt-revision__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="prompt-revision__steps">
          <div className={`prompt-revision__step ${step >= 1 ? 'active' : ''}`}>
            <span className="prompt-revision__step-num">1</span>
            <span className="prompt-revision__step-label">What to Change</span>
          </div>
          <div className="prompt-revision__step-line" />
          <div className={`prompt-revision__step ${step >= 2 ? 'active' : ''}`}>
            <span className="prompt-revision__step-num">2</span>
            <span className="prompt-revision__step-label">Review & Refine</span>
          </div>
        </div>

        {/* Step Content */}
        {step === 1 ? renderIntentStep() : renderReviewStep()}
      </div>
    </div>
  );
};

export default PromptRevisionModal;
